import {
  applySnapshot,
  getSnapshot,
  Instance,
  SnapshotIn,
  SnapshotOut,
  types,
} from "mobx-state-tree";
import {
  createSurveyRuleModel,
  SurveyRuleModel,
} from "../models/SurveyRuleModel";
import { SurveyRule } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRule";
import {
  createGetSurveyRulesStore,
  GetSurveyRulesStore,
} from "./GetSurveyRulesStore";
import {
  mapToSurveyRuleModel,
  mapToSurveyRuleRPCObject,
} from "../../utils/SurveyRuleMappers";
import { SurveyRuleValidationErrorGroup } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleValidationErrorGroup";
import { SurveyRuleAction } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleAction";
import { SurveyRuleTargetModel } from "../models/SurveyRuleTargetModel";
import { SurveyRuleConditionModel } from "../models/SurveyRuleConditionModel";
import {
  createSaveSurveyRulesStore,
  SaveSurveyRulesStore,
} from "./SaveSurveyRulesStore";
import {
  createGetSurveyRuleKeyOptionsStore,
  GetSurveyRuleKeyOptionsStore,
} from "./GetSurveyRuleKeyOptionsStore";
import {
  createGetSurveyRuleValueOptionsStore,
  GetSurveyRuleValueOptionsStore,
} from "./GetSurveyRuleValueOptionsStore";
import {
  createGetSurveyRuleTargetOptionsStore,
  GetSurveyRuleTargetOptionsStore,
} from "./GetSurveyRuleTargetOptionsStore";

interface SurveyRuleStoreInitialState {
  rules: Record<string, SnapshotIn<typeof SurveyRuleModel>>;
  rulesOrder: string[];
  getSurveyRulesStore: Instance<typeof GetSurveyRulesStore>;
  getSurveyRuleKeyOptionsStore: Instance<typeof GetSurveyRuleKeyOptionsStore>;
  getSurveyRuleValueOptionsStore: Instance<
    typeof GetSurveyRuleValueOptionsStore
  >;
  getSurveyRuleTargetOptionsStore: Instance<
    typeof GetSurveyRuleTargetOptionsStore
  >;
  saveSurveyRulesStore: Instance<typeof SaveSurveyRulesStore>;
}

const getInitialState = (): SurveyRuleStoreInitialState => ({
  rules: {},
  rulesOrder: [],
  getSurveyRulesStore: createGetSurveyRulesStore(),
  getSurveyRuleKeyOptionsStore: createGetSurveyRuleKeyOptionsStore(),
  getSurveyRuleValueOptionsStore: createGetSurveyRuleValueOptionsStore(),
  getSurveyRuleTargetOptionsStore: createGetSurveyRuleTargetOptionsStore(),
  saveSurveyRulesStore: createSaveSurveyRulesStore(),
});

const hasConditionError = (
  condition: Instance<typeof SurveyRuleConditionModel>,
): boolean => {
  const { keyType } = condition;

  return (
    !keyType.conditionKey ||
    !keyType.conditionValue.length ||
    keyType.conditionKeyValidationError.length > 0 ||
    keyType.conditionValueValidationError.length > 0 ||
    !!condition.connectorValidationError
  );
};

const hasRuleError = (rule: Instance<typeof SurveyRuleModel>): boolean => {
  return (
    !rule.action ||
    rule.target.length === 0 ||
    Array.from(rule.conditions.values()).some(hasConditionError) ||
    rule.actionValidationErrors.length > 0 ||
    rule.targetValidationErrors.length > 0
  );
};

const clearSaveErrors = (store: {
  saveSurveyRulesStore: Instance<typeof SaveSurveyRulesStore>;
}): void => {
  store.saveSurveyRulesStore.clearErrors();
};

export const SurveyRuleStore = types
  .model("SurveyRuleStore", {
    rules: types.map(SurveyRuleModel),
    rulesOrder: types.array(types.string),
    getSurveyRulesStore: GetSurveyRulesStore,
    getSurveyRuleKeyOptionsStore: GetSurveyRuleKeyOptionsStore,
    getSurveyRuleValueOptionsStore: GetSurveyRuleValueOptionsStore,
    getSurveyRuleTargetOptionsStore: GetSurveyRuleTargetOptionsStore,
    saveSurveyRulesStore: SaveSurveyRulesStore,
  })
  .views((store) => ({
    get disableSaveRules(): boolean {
      return (
        Array.from(store.rules.values()).some(hasRuleError) ||
        store.saveSurveyRulesStore.maxLimitExceededError !== null ||
        store.saveSurveyRulesStore.saveSurveyRulesRPCError !== null
      );
    },
    get rulesRPCObject(): SurveyRule[] {
      return store.rulesOrder.map((ruleId) => {
        const rule = store.rules.get(ruleId);
        if (!rule) {
          throw new Error(`Survey rule with ID ${ruleId} not found`);
        }
        return mapToSurveyRuleRPCObject(rule);
      });
    },
  }))
  .actions((store) => ({
    getNonNullRule: (ruleId: string): Instance<typeof SurveyRuleModel> => {
      const rule = store.rules.get(ruleId);
      if (!rule) {
        throw new Error(`Survey rule with ID ${ruleId} not found`);
      }
      return rule;
    },
  }))
  .actions((store) => ({
    setRuleKeyType: (
      ruleId: string,
      conditionId: string,
      keyType: "QUESTION" | "RESPONDENT",
    ): void => {
      const rule = store.getNonNullRule(ruleId);
      rule.clearValidationErrors();
      clearSaveErrors(store);
      const condition = rule.getNonNullCondition(conditionId);
      condition.setKeyType(keyType);
      if (
        keyType === "RESPONDENT" &&
        rule.action === SurveyRuleAction.SurveyRuleAction.SKIP_TO
      ) {
        rule.clearAction();
      }
    },
    setRuleKey: (
      ruleId: string,
      conditionId: string,
      key: string | undefined,
    ): void => {
      const rule = store.getNonNullRule(ruleId);
      rule.clearValidationErrors();
      clearSaveErrors(store);
      const condition = rule.getNonNullCondition(conditionId);
      condition.keyType.setConditionKey(key);
    },
    setRuleValue: (
      ruleId: string,
      conditionId: string,
      value: string[],
    ): void => {
      const rule = store.getNonNullRule(ruleId);
      rule.clearValidationErrors();
      clearSaveErrors(store);
      const condition = rule.getNonNullCondition(conditionId);
      condition.keyType.setConditionValue(value);
    },
    setAction: (
      ruleId: string,
      action: SurveyRuleAction.SurveyRuleAction,
    ): void => {
      const rule = store.getNonNullRule(ruleId);
      rule.clearValidationErrors();
      clearSaveErrors(store);
      rule.setAction(action);
    },
    setTarget: (
      ruleId: string,
      target: SnapshotOut<typeof SurveyRuleTargetModel>[],
    ): void => {
      const rule = store.getNonNullRule(ruleId);
      rule.clearValidationErrors();
      clearSaveErrors(store);
      rule.setTarget(target);
    },
    setRules(rules: SurveyRule[]): void {
      const ruleEntries = rules.map(mapToSurveyRuleModel);
      store.rules.replace(Object.fromEntries(ruleEntries));
      store.rulesOrder.replace(ruleEntries.map(([ruleId]) => ruleId));
    },
    addNewRule(type: "SINGLE" | "GROUP", order?: number): void {
      clearSaveErrors(store);
      const newRule = createSurveyRuleModel();
      Array.from({ length: type === "SINGLE" ? 1 : 2 }, () => {
        newRule.addNewCondition();
      });

      order
        ? store.rulesOrder.splice(order, 0, newRule.ruleId)
        : store.rulesOrder.push(newRule.ruleId);
      store.rules.put(newRule);
    },
    removeSurveyRule(ruleId: string): void {
      store.rulesOrder.remove(ruleId);
      store.rules.delete(ruleId);
      clearSaveErrors(store);
    },
    reorderRules(ruleId: string, order: number): void {
      const currentIndex = store.rulesOrder.indexOf(ruleId);

      if (currentIndex === -1) {
        throw new Error(`Survey rule with ID '${ruleId}' not found.`);
      }

      store.rulesOrder.splice(currentIndex, 1);
      store.rulesOrder.splice(order, 0, ruleId);
    },
    addNewCondition(ruleId: string): void {
      const rule = store.getNonNullRule(ruleId);
      rule.clearValidationErrors();
      clearSaveErrors(store);
      rule.addNewCondition();
    },
    removeSurveyCondition(ruleId: string, conditionId: string): void {
      const rule = store.getNonNullRule(ruleId);
      rule.clearValidationErrors();
      clearSaveErrors(store);
      rule.removeCondition(conditionId);
    },
    setValidationErrors: (
      validationErrorGroups: SurveyRuleValidationErrorGroup[],
    ): void => {
      store.rules.forEach((rule) => {
        rule.clearValidationErrors();
      });
      validationErrorGroups.forEach(({ ruleId, target }) => {
        const rule = store.getNonNullRule(ruleId.uuid);
        rule.setValidationErrors(target);
      });
    },
    resetStore: (): void => {
      const { rules, rulesOrder } = getInitialState();
      store.getSurveyRulesStore.resetStore();
      store.getSurveyRuleKeyOptionsStore.resetStore();
      store.getSurveyRuleValueOptionsStore.resetStore();
      store.getSurveyRuleTargetOptionsStore.resetStore();
      store.saveSurveyRulesStore.resetStore();
      applySnapshot(store, {
        ...getSnapshot(store),
        rules,
        rulesOrder,
      });
    },
  }));

export const createSurveyRuleStore = (): Instance<typeof SurveyRuleStore> =>
  SurveyRuleStore.create(getInitialState());
